import { WSLabelProps } from "@wingspanhq/fe-component-library";
import { ICreditCard } from "@wingspanhq/users/dist/lib/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import capitalize from "lodash/capitalize";
import { useHistory } from "react-router-dom";
import {
  ExternalBankAccount,
  ExternalBankAccountStatus,
  InternalAccount,
  PaymentCard
} from "@wingspanhq/wingspan-api-sdk";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarPlatformBalance,
  avatarTaxWithholding,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { creditCardBrandNameMap } from "../../../../shared/constants/bankCard";
import { selectorExternalBankAccountInstitution } from "@selectors/selectorExternalBankAccountInstitution";
import { selectorExternalBankAccountName } from "@selectors/selectorExternalBankAccountName";
import { selectorExternalBankAccountVerificationPending } from "@selectors/selectorExternalBankAccountVerificationPending";
import { PATH_PLATFORM_BALANCE } from "../../../PlatformBalance/paths";
import { PATH_TAX_WITHHOLDINGS } from "../../../TaxWithholdings/paths";
import { ListItemProps, MethodActionHandlers, MethodsData } from "./types";

export function getAccountLabelProps(
  externalBankAccount: ExternalBankAccount
): WSLabelProps {
  return {
    text: selectorExternalBankAccountInstitution(externalBankAccount),
    secondaryText: selectorExternalBankAccountName(externalBankAccount),
    avatar: avatarBankAccount
  };
}

export function getInternalAccountLabelProps(
  internalAccount: InternalAccount,
  historyPush?: ReturnType<typeof useHistory>["push"],
  balance?: number
): WSLabelProps {
  const mask = internalAccount.numbers?.account?.slice(-4);
  const maskText = mask ? `(${mask})` : undefined;
  const balanceText =
    balance !== undefined
      ? `${formatMoney(balance, internalAccount.currency)} is available`
      : undefined;

  switch (internalAccount.type) {
    case "TaxWithholding":
      return {
        link: {
          label: "Tax Withholding",
          onClick: historyPush
            ? () => {
                historyPush(PATH_TAX_WITHHOLDINGS);
              }
            : undefined
        },
        secondaryText: maskText,
        helperText: balanceText,
        avatar: avatarTaxWithholding
      };

    case "ApPayouts":
      return {
        link: {
          label: `${internalAccount.currency} Platform balance`,
          onClick: historyPush
            ? () => {
                historyPush(PATH_PLATFORM_BALANCE);
              }
            : undefined
        },
        helperText: balanceText,
        avatar: avatarPlatformBalance
      };

    default:
      return {
        link: {
          label: "Wingspan Wallet",
          onClick: historyPush
            ? () => {
                historyPush("/member/wallet");
              }
            : undefined
        },
        secondaryText: maskText,
        helperText: balanceText,
        avatar: avatarWallet
      };
  }
}

// Card expiration is exposed differently across card models (a single "MM/YY"
// string on credit cards, split MM + YYYY on payment cards). Normalize them all
// to a compact "Exp MM/YY" label.
const formatCardExpiration = (
  month?: string | number,
  year?: string | number
): string | undefined => {
  if (
    month === undefined ||
    month === null ||
    month === "" ||
    year === undefined ||
    year === null ||
    year === ""
  ) {
    return undefined;
  }

  const mm = String(month).padStart(2, "0");
  const yy = String(year).slice(-2);

  return `Exp ${mm}/${yy}`;
};

export function getCreditCardLabelProps(creditCard: ICreditCard): WSLabelProps {
  return {
    text: creditCardBrandNameMap[creditCard.cardBrand || ""] || "Credit Card",
    secondaryText: creditCard.last4 ? `(${creditCard.last4})` : undefined,
    helperText: creditCard.expiryDate
      ? `Exp ${creditCard.expiryDate}`
      : undefined,
    avatar: avatarBankCard
  };
}

export function getPaymentCardLabelProps(
  paymentCard: PaymentCard
): WSLabelProps {
  return {
    text: capitalize(paymentCard.brand || "card"),
    secondaryText: paymentCard.last4 ? `(${paymentCard.last4})` : undefined,
    helperText: formatCardExpiration(
      paymentCard.expirationMonth,
      paymentCard.expirationYear
    ),
    avatar: avatarBankCard
  };
}

export function getMethods(
  data: MethodsData,
  handlers?: MethodActionHandlers,
  category?: string
) {
  const items: ListItemProps[] = [];

  data.accounts?.forEach(externalBankAccount => {
    const microdepositsVerificationPending =
      selectorExternalBankAccountVerificationPending(externalBankAccount);

    const hasNoNumber = !externalBankAccount.accountNumberMask;

    items.push({
      id: externalBankAccount.id,
      type: "Account",
      label: getAccountLabelProps(externalBankAccount),
      pill: hasNoNumber
        ? {
            theme: "warning",
            icon: true,
            text: "Missing information"
          }
        : undefined,
      country: "US",
      category,
      actions: {
        select: hasNoNumber
          ? undefined
          : () => {
              handlers?.handleChange?.({
                id: externalBankAccount.id,
                type: "Account",
                category
              });
            },

        blocked: hasNoNumber
          ? () => handlers?.notifyAccountSelectionBlocked?.()
          : undefined,

        remove: () => {
          handlers?.openModalRemove?.({
            id: externalBankAccount.id,
            type: "Account",
            category
          });
        },

        resync:
          hasNoNumber && !!handlers?.openPlaid
            ? () => handlers?.openPlaid!()
            : undefined,

        reconnect:
          externalBankAccount.status ===
            ExternalBankAccountStatus.DISCONNECTED &&
          !!handlers?.openPlaidReconnect
            ? () => handlers?.openPlaidReconnect!(externalBankAccount)
            : undefined,

        verify:
          microdepositsVerificationPending &&
          !!handlers?.openMicrodepositsVerify
            ? () => {
                handlers?.openMicrodepositsVerify!({
                  externalBankAccount
                });
              }
            : undefined
      }
    });
  });

  data.internalAccounts?.forEach(internalAccount => {
    items.push({
      id: internalAccount.id,
      type: "InternalAccount",
      label: getInternalAccountLabelProps(
        internalAccount,
        handlers?.historyPush
      ),
      country: internalAccount.type === "ApPayouts" ? "Platform" : "US",
      category,
      actions: {
        select: () => {
          handlers?.handleChange?.({
            id: internalAccount.id,
            type: "InternalAccount",
            category
          });
        }
      }
    });
  });

  data.creditCards?.forEach(creditCard => {
    items.push({
      id: creditCard.paymentMethodId || "",
      type: "CreditCard",
      label: getCreditCardLabelProps(creditCard),
      country: "US",
      category,
      actions: {
        select: () => {
          handlers?.handleChange?.({
            id: creditCard.paymentMethodId || "",
            type: "CreditCard",
            category
          });
        },
        remove: () => {
          handlers?.openModalRemove?.({
            id: creditCard.paymentMethodId || "",
            type: "CreditCard",
            category
          });
        }
      }
    });
  });

  data.paymentCards?.forEach(paymentCard => {
    items.push({
      id: paymentCard.id,
      type: "PaymentCard",
      // v1 exposed the card's own country; v3 only carries the cardholder's
      // billing address.
      country: paymentCard.billingAddress?.country || "US",
      label: getPaymentCardLabelProps(paymentCard),
      category,
      actions: {
        select: () => {
          handlers?.handleChange?.({
            id: paymentCard.id,
            type: "PaymentCard",
            category
          });
        },
        remove: () => {
          handlers?.openModalRemove?.({
            id: paymentCard.id,
            type: "PaymentCard",
            category
          });
        }
      }
    });
  });

  const methodsByCountry: Record<string, typeof items> = {};

  items.forEach(item => {
    if (!methodsByCountry[item.country]) {
      methodsByCountry[item.country] = [];
    }

    methodsByCountry[item.country].push(item);
  });

  // Convert object to array
  const methodsByCountryArray = Object.entries(methodsByCountry).map(
    ([country, methods]) => ({
      country,
      methods
    })
  );

  methodsByCountryArray.sort((a, b) => {
    if (a.country === "Platform" || a.country === "US") {
      return -1;
    }

    if (b.country === "Platform" || b.country === "US") {
      return 1;
    }

    return a.country.localeCompare(b.country);
  });

  return methodsByCountryArray;
}
